/**
 * Implements software requirements: SRS-215
 */

import { defineStore } from 'pinia';
import { computed, type ComputedRef, onScopeDispose, ref, type Ref } from 'vue';
import { type RouteLocationRaw, useRoute } from 'vue-router';
import { watchImmediate } from '@vueuse/core';
import { verify } from '@/util/verify';
import type { CaseDetailsApiObjectBase, CaseNotification } from '@/api/caseSettings';
import { CaseStatus } from '@/api/caseSearch';
import { ROUTES } from '@/router';
import { enabledActionsByStatus } from '@/stores/enabledActionsByStatus';
import { asyncTimeout } from '@/util/asyncTimeout';
import { logger } from '@/util/logger';
import { type AxiosRequestConfig, type AxiosResponse, HttpStatusCode } from 'axios';
import { client } from '@/api/http';
import { onCaseStale, releaseCaseVersion, seedCaseVersion } from '@/api/caseVersion';
import { AppError } from '@/stores/appErrorStore';
import { errorDetail } from '@/api/errorDetail';

export const caseCompletedMessage = 'Case processing complete, ready for planning';

const log = logger('poll-case');
const POLL_INTERVAL_MILLISECONDS = 30 * 1000;

export const useCaseSyncStore = defineStore('case-sync', () => {
    const route = useRoute();
    const case_ = ref<CaseDetailsApiObjectBase | null>(null);
    const isCaseSpecific = ref<boolean>(false);

    let cancellation: AbortController | null = null;
    const caseVisits = _caseVisits();
    const isStale = _staleCaseFlag(caseVisits.current, () => cancellation?.abort());

    function _clearState() {
        case_.value = null;
        cancellation?.abort();
    }

    async function _startCaseSync(caseId: string): Promise<void> {
        cancellation?.abort();
        if (isStale.value) return;
        cancellation = new AbortController();

        const _onFetch = (data: CaseDetailsApiObjectBase | null) => {
            if (data) {
                seedCaseVersion(Number(caseId), data.case_version);
                case_.value = data;
            } else {
                cancellation?.abort();
                case_.value = null;
            }
        };

        await _pollCase(Number(caseId), _onFetch, cancellation.signal);
    }

    const unwatch = watchImmediate(
        () => [route.meta.caseSpecific, route.params.id],
        async ([caseSpecific, caseId]) => {
            isCaseSpecific.value = !!caseSpecific;

            if (caseSpecific) {
                const id = verify(caseId as string, 'caseId should exist on case-specific routes');
                caseVisits.enter(Number(id));
                await _startCaseSync(id);
            } else {
                caseVisits.enter(null);
                _clearState();
            }
        },
    );

    async function refresh() {
        if (case_.value?.id) {
            await _startCaseSync(case_.value.id.toString());
        }
    }

    return {
        case_,
        ..._caseFields(case_),
        isCaseSpecific,
        isStale: computed<boolean>(() => isStale.value),
        ..._caseStatusFlags(case_),
        refresh,
        stop: () => {
            unwatch();
            cancellation?.abort();
        },
    };
});

/**
 * Once stale, the case is not fetched again, so no read can hide the conflict before the reload.
 * A refusal for a case the session left blocks that case only, not the one open now.
 */
function _staleCaseFlag(
    currentCaseId: Readonly<Ref<number | null>>,
    stopPolling: () => void,
): ComputedRef<boolean> {
    const staleCaseIds = ref<Set<number>>(new Set());
    onScopeDispose(
        onCaseStale((error) => {
            staleCaseIds.value.add(error.caseId);
            if (error.caseId === currentCaseId.value) stopPolling();
        }),
    );
    return computed<boolean>(
        () => currentCaseId.value !== null && staleCaseIds.value.has(currentCaseId.value),
    );
}

/**
 * Leaving a case releases its version, because its pages load it again on return. Moving between
 * pages of the same case keeps the version.
 */
function _caseVisits(): {
    current: Readonly<Ref<number | null>>;
    enter: (caseId: number | null) => void;
} {
    const current = ref<number | null>(null);
    return {
        current,
        enter: (caseId: number | null) => {
            if (current.value !== null && current.value !== caseId) {
                releaseCaseVersion(current.value);
            }
            current.value = caseId;
        },
    };
}

function _caseFields(case_: Ref<CaseDetailsApiObjectBase | null>) {
    return {
        notifications: computed<CaseNotification[]>(() => case_.value?.notifications ?? []),
        caseName: computed<string | null>(() => case_.value?.name ?? null),
        study: computed<number | null>(() => case_.value?.study ?? null),
        hipSpineStudyId: computed<number | null>(() => case_.value?.hip_spine_study_id ?? null),
        routeTo: (route: ROUTES): RouteLocationRaw | undefined => _routeTo(case_.value, route),
        notificationsByStudy: (study: number | null): CaseNotification[] =>
            case_.value?.notifications.filter((n) => n.study === study) ?? [],
    };
}

function _routeTo(
    case_: CaseDetailsApiObjectBase | null,
    route: ROUTES,
): RouteLocationRaw | undefined {
    if (case_ === null) {
        return undefined;
    }
    const actions = enabledActionsByStatus(case_.status);
    return actions.includes(route) ? { name: route, params: { id: case_.id } } : undefined;
}

function _caseStatusFlags(case_: Ref<CaseDetailsApiObjectBase | null>) {
    const statusState = computed<CaseStatus>(() => {
        return case_.value?.status ?? CaseStatus.Error;
    });

    function caseHasStatusState(...states: CaseStatus[]): boolean {
        return states.find((s) => s === statusState.value) !== undefined;
    }

    return {
        statusState,
        isCaseComplete: computed(() => caseHasStatusState(CaseStatus.Completed)),
        isCaseWarning: computed(() => caseHasStatusState(CaseStatus.Warning)),
        isCaseProcessing: computed(() =>
            caseHasStatusState(
                CaseStatus.GeneratingDicoms,
                CaseStatus.ImageSegmentation,
                CaseStatus.SurgicalTemplating,
                CaseStatus.GeneratingPlan,
            ),
        ),
        isSegmentationComplete: computed(
            () => !caseHasStatusState(CaseStatus.GeneratingDicoms, CaseStatus.ImageSegmentation),
        ),
        isCaseNew: computed(() => caseHasStatusState(CaseStatus.Created)),
        isCaseError: computed(() => caseHasStatusState(CaseStatus.Error)),
    };
}

async function _getCaseDetails(
    id: number,
    config?: AxiosRequestConfig,
): Promise<CaseDetailsApiObjectBase> {
    return client.get(`/cases/${id}`, config).then(({ status, data }: AxiosResponse) => {
        if (status === HttpStatusCode.Ok) {
            return data;
        } else if (status === HttpStatusCode.NotFound || status === HttpStatusCode.Forbidden) {
            throw new AppError(
                'Case not found' + errorDetail(data),
                'This case does not exist or you do not have permission to view it.',
            );
        }

        throw Error(`Failed to load case settings: ${data?.detail}`);
    });
}

/**
 * Repeatedly fetch a case until it is cancelled
 */
async function _pollCase(
    caseId: number,
    onFetch: (case_: CaseDetailsApiObjectBase | null) => void,
    signal: AbortSignal,
): Promise<void> {
    try {
        while (!signal.aborted) {
            // Attempt to fetch the case
            const case_ = (await _getCaseDetails(caseId, { signal })) ?? null;

            signal.throwIfAborted();

            onFetch(case_);

            signal.throwIfAborted();

            await asyncTimeout(POLL_INTERVAL_MILLISECONDS, signal);
        }
    } catch (e: unknown) {
        if (signal.aborted) {
            log.debug('case polling cancelled');
            return;
        }

        throw e;
    }
}
